import { EmojiResource } from '@atlaskit/emoji/resource';

import { getEmojiProvider as fabricGetEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';

import { EMOJI_TITLE_PUBLISHED_FIELD } from './useEmojiStatusFieldName.ts';
import type {
	getEmojiTitleProperty_content_nodes_emojiTitlePublished_nodes,
	getEmojiTitleProperty as getEmojiTitlePropertyType,
} from './emoji-title/__types__/getEmojiTitleProperty.ts';
import type { EmojiStatusFieldName } from './__types__/EmojiStatusFieldName.ts';

type EmojiTitleProperty<TContentNode> = {
	content: {
		nodes: (TContentNode | null)[] | null;
	} | null;
	singleContent?: TContentNode | null;
};

type EmojiTitlePublishedPropertyNode<
	TEmojiNode = getEmojiTitleProperty_content_nodes_emojiTitlePublished_nodes,
> = {
	id: string | null;
	emojiTitlePublished: {
		nodes: (TEmojiNode | null)[] | null;
	} | null;
};

export const getVersionNumber = (
	data: getEmojiTitlePropertyType,
	emojiFieldName: EmojiStatusFieldName,
) => data?.content?.nodes?.[0]?.[emojiFieldName]?.nodes?.[0]?.version?.number || 0;

export const getEmojiId = (data: getEmojiTitlePropertyType, emojiFieldName: EmojiStatusFieldName) =>
	(data?.content?.nodes?.[0]?.[emojiFieldName]?.nodes?.[0]?.value || '').replace(/['"]+/g, '');

export const sanitizeEmojiValue = (emojiValue: string | null | undefined) =>
	(emojiValue || '').replace(/['"]+/g, '');

export type EmojiProperty = {
	propertyId: string;
	value: string;
	versionNumber: number;
};

type EmojiPropertyType = getEmojiTitleProperty_content_nodes_emojiTitlePublished_nodes;

const transformProperty = (property: EmojiPropertyType | undefined): EmojiProperty | undefined => {
	if (property) {
		return {
			propertyId: property?.id ?? '',
			value: (property?.value || '').replace(/['"]+/g, '').replace(/null/, ''),
			versionNumber: property?.version?.number || 0,
		};
	}
};

type ObjectWithEmojiProperties = {
	properties?: { nodes?: EmojiPropertyType[] } | null;
} | null;

const findPropertyByKey = (object: ObjectWithEmojiProperties, key: string) =>
	transformProperty(object?.properties?.nodes?.find((node) => node.key === key));

export const getEmojiPublishedProperty = (object: ObjectWithEmojiProperties) =>
	findPropertyByKey(object, 'emoji-title-published');

export const getEmojiDraftProperty = (object: ObjectWithEmojiProperties) =>
	findPropertyByKey(object, 'emoji-title-draft');

export const getEmojiFromPublishedPageProperties = (page: ObjectWithEmojiProperties) => {
	const values = page?.properties?.nodes?.filter((node) => node.key === 'emoji-title-published');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getEmojiFromDraftPageProperties = (page: ObjectWithEmojiProperties) => {
	const values = page?.properties?.nodes?.filter((node) => node.key === 'emoji-title-draft');
	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getPublishedEmojiId = (
	data:
		| EmojiTitleProperty<
				EmojiTitlePublishedPropertyNode<
					Pick<getEmojiTitleProperty_content_nodes_emojiTitlePublished_nodes, 'value'>
				>
		  >
		| undefined
		| null,
) =>
	(
		(data?.content?.nodes?.[0] || data?.singleContent)?.emojiTitlePublished?.nodes?.[0]?.value || ''
	).replace(/['"]+/g, '');

type EmojiNodesContent =
	| {
			emoji?: {
				nodes?: Array<{
					value?: string | null;
					key?: string | null;
				} | null> | null;
			} | null;
	  }
	| {
			readonly emoji?: {
				readonly nodes?:
					| readonly (
							| {
									readonly value?: string | null;
									readonly id?: string | null;
							  }
							| null
							| undefined
					  )[]
					| null;
			} | null;
	  };

type EmojiDraftNodesContent = {
	emojiDraft?: {
		nodes?: Array<{
			value: string;
			key: string;
		}>;
	};
};

export const getEmojiValueFromContent = (content?: EmojiNodesContent | null) =>
	(content?.emoji?.nodes?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');

export const getEmojiDraftValueFromContent = (content: EmojiDraftNodesContent) =>
	(content?.emojiDraft?.nodes?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');

export const getEmojiDraftFromProperties = (content: EmojiNodesContent) => {
	const values = content?.emoji?.nodes?.filter(
		(node) => node && 'key' in node && node?.key === 'emoji-title-draft',
	);

	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getEmojiPublishedFromProperties = (content: EmojiNodesContent) => {
	const values = content?.emoji?.nodes?.filter(
		(node) => node && 'key' in node && node?.key === 'emoji-title-published',
	);

	return (values?.[0]?.value || '').replace(/['"]+/g, '').replace(/null/, '');
};

export const getHasEmoji = (
	data: getEmojiTitlePropertyType,
	emojiFieldName: EmojiStatusFieldName,
) => {
	const nodes = (data?.content?.nodes?.[0] || data?.singleContent)?.[emojiFieldName]?.nodes?.[0];

	return !!nodes?.value && nodes?.value !== '""' && nodes?.value !== '"null"';
};

export const getHasEmojiPublished = (data: getEmojiTitlePropertyType) =>
	getHasEmoji(data, EMOJI_TITLE_PUBLISHED_FIELD);

export const getEmojiProvider = async (cloudId: string, userId: string) => {
	return await fabricGetEmojiProvider(EmojiResource, {
		cloudId,
		userId,
	});
};

export enum DefaultTitleEmojiValues {
	LIVE_PAGE_DEFAULT = 'live-page-default',
	NONE = 'none',
}

export const getHasLiveDocsDefaultEmoji = (data?: getEmojiTitlePropertyType) => {
	if (!data) {
		return false;
	}
	const defaultTitleEmoji = data?.content?.nodes?.[0]?.contentProperties?.latest?.defaultTitleEmoji;

	// This property denotes whether or not we show a default emoji icon, and is initially set to null to avoid backfilling on the BE;
	// therefore, a null value OR a specifically set value implies that we need to show a default icon, while a "none" value implies
	// that we should not show an icon. The default icon should also be overriden by a user-set emoji.
	return (
		defaultTitleEmoji === null || defaultTitleEmoji === DefaultTitleEmojiValues.LIVE_PAGE_DEFAULT
	);
};
